import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "timesheet-explorer" }
const _hoisted_2 = { class: "filters-block" }
const _hoisted_3 = { class: "section-title" }
const _hoisted_4 = { class: "period-section" }
const _hoisted_5 = { class: "period-mode-row" }
const _hoisted_6 = { class: "filter-label" }
const _hoisted_7 = { class: "period-mode-buttons" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = {
  key: 0,
  class: "period-navigation"
}
const _hoisted_10 = { class: "period-label" }
const _hoisted_11 = {
  key: 1,
  class: "custom-period-row"
}
const _hoisted_12 = {
  key: 2,
  class: "all-time-label"
}
const _hoisted_13 = { class: "filters-row" }
const _hoisted_14 = { key: 0 }
const _hoisted_15 = { value: "" }
const _hoisted_16 = ["value"]
const _hoisted_17 = { value: "" }
const _hoisted_18 = ["value"]
const _hoisted_19 = { value: "" }
const _hoisted_20 = ["value"]
const _hoisted_21 = { class: "keyword-filter" }
const _hoisted_22 = ["placeholder"]
const _hoisted_23 = { value: undefined }
const _hoisted_24 = { value: true }
const _hoisted_25 = { value: false }
const _hoisted_26 = { class: "view-block" }
const _hoisted_27 = { class: "section-title" }
const _hoisted_28 = { class: "grouping-row" }
const _hoisted_29 = ["disabled", "onClick"]
const _hoisted_30 = ["disabled", "onClick"]
const _hoisted_31 = ["onClick"]
const _hoisted_32 = { value: "" }
const _hoisted_33 = ["value"]
const _hoisted_34 = { class: "results-block" }
const _hoisted_35 = { class: "summary-row" }
const _hoisted_36 = { class: "summary-amount" }
const _hoisted_37 = {
  key: 0,
  class: "state-row error-row"
}
const _hoisted_38 = {
  key: 1,
  class: "state-row"
}
const _hoisted_39 = {
  key: 2,
  class: "state-row"
}
const _hoisted_40 = {
  key: 4,
  class: "entries-table"
}
const _hoisted_41 = ["onClick"]
const _hoisted_42 = { class: "entry-notes" }
const _hoisted_43 = { class: "entry-amount" }
const _hoisted_44 = {
  key: 5,
  class: "load-more-row"
}

import { computed, onMounted, reactive, ref, watch } from "vue";
import { useApolloClient } from "@vue/apollo-composable";
import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { logErrorMessages } from "@vue/apollo-util";

import type { DocumentNode } from "graphql";
import BasicButton from "@/components/BasicButton.vue";
import BasicInput from "@/components/form/BasicInput.vue";
import TimesheetGroupNode from "@/components/timesheets/TimesheetGroupNode.vue";
import EditTimesheet from "@/components/CRUXEntity/EditTimesheet.vue";
import {
  GET_THEMES,
  GET_TIMESHEET_EXPLORER_ENTRIES,
  GET_TIMESHEET_GROUPS,
} from "@/graphql/timesheets/queries";
import {
  GET_EMPLOYEE_PROJECTS,
  GET_PROJECT_USERS,
  GET_PROJECT_THEMES,
} from "@/graphql/project/queries";
import { GET_EMPLOYEES } from "@/graphql/user/queries";
import { formatDurationMinutes } from "@/helpers/dates";
import { formatPrice } from "@/helpers/utils";
import timesheetI18n from "@/locales/timesheets/en-UK.json";
import type { TimesheetFormValue } from "@/helpers/client/typesDefinition";
import { getUser, isAdmin } from "@/helpers/router/queries";
import useHasAccess from "@/composables/useHasAccess";

type MessageSchema = typeof timesheetI18n;

type TimesheetGroupDimension = "PROJECT" | "USER" | "THEMATIC";
type PeriodMode = "WEEK" | "MONTH" | "YEAR" | "CUSTOM" | "ALL_TIME";

type TimesheetGroup = {
  key: string;
  id: string | null;
  label: string;
  duration: number;
  count: number;
  latestDate: string | null;
  childType: "GROUP" | "TIMESHEET";
  amount: number;
};

type TimesheetEntry = {
  id: string;
  date: string;
  duration: number;
  notes?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  userId?: string | null;
  userName?: string | null;
  thematicId?: string | null;
  thematicName?: string | null;
  roleId?: string | null;
  roleName?: string | null;
  hourlyRate?: number | null;
  amount: number;
};

type ProjectOption = {
  id: string;
  name: string;
};

type UserOption = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
};

type ThemeOption = {
  id: string;
  name: string;
  project?: {
    id: string;
    name?: string | null;
  } | null;
};

const PAGE_SIZE = 20;


export default /*@__PURE__*/_defineComponent({
  __name: 'TimesheetExplorer',
  props: {
    projectId: {}
  },
  setup(__props: any) {

dayjs.extend(isoWeek);

const { t } = useI18n<[MessageSchema], "en-UK">({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": timesheetI18n,
  },
});

dayjs.extend(isoWeek);

const formatAmount = (amount: number) => formatPrice(amount);

const props = __props;

const { client } = useApolloClient();
const toast = useToast();

const periodMode = ref<PeriodMode>("MONTH");
const periodCursor = ref<Dayjs>(dayjs());

const isProjectFixed = computed(() => !!props.projectId);

const filters = reactive({
  customFromDate: "",
  customToDate: "",
  projectId: props.projectId ?? "",
  userId: "",
  thematicId: "",
  billable: undefined as boolean | undefined,
  keyword: "",
});

// Prevent user from changing project filter when fixed
watch(
  () => filters.projectId,
  (newVal) => {
    if (props.projectId && newVal !== props.projectId) {
      filters.projectId = props.projectId;
    }
  },
);

const groupBy = ref<TimesheetGroupDimension[]>(["USER"]);

const groups = ref<TimesheetGroup[]>([]);
const entries = ref<TimesheetEntry[]>([]);
const totalDuration = ref(0);
const totalCount = ref(0);
const totalItems = ref(0);
const totalAmount = ref(0);
const rootOffset = ref(0);
const refreshVersion = ref(0);
const loading = ref(false);
const error = ref<string | null>(null);

const projects = ref<ProjectOption[]>([]);
const themes = ref<ThemeOption[]>([]);
const employees = ref<UserOption[]>([]);

// Modal state for EditTimesheet
const showEditTimesheetModal = ref(false);
const selectedTimesheetForEdit = ref<TimesheetFormValue | null>(null);
const isUserAdmin = ref(false);
const isDeliveryManager = ref(false);

const { hasDeliveryManagerAccess } = useHasAccess(() => props.projectId ?? "");

const editInModal = computed(
  () => isUserAdmin.value || isDeliveryManager.value,
);

watch(hasDeliveryManagerAccess, (newVal) => {
  isDeliveryManager.value = newVal;
});

onMounted(async () => {
  const user = await getUser();
  isUserAdmin.value = isAdmin(user.accessRights);
});

const handleOpenEntry = (entry: TimesheetEntry) => {
  if (!editInModal.value) return;

  selectedTimesheetForEdit.value = {
    id: entry.id,
    date: entry.date,
    duration: entry.duration,
    notes: entry.notes ?? "",
    userId: entry.userId ?? null,
    userName: entry.userName ?? null,
    projectId: entry.projectId ?? null,
    themeId: entry.thematicId ?? null,
    roleId: entry.roleId ?? null,
    activityId: null,
  };
  showEditTimesheetModal.value = true;
};

const closeEditTimesheetModal = () => {
  showEditTimesheetModal.value = false;
  selectedTimesheetForEdit.value = null;
};

const handleTimesheetUpdated = () => {
  closeEditTimesheetModal();
  refreshVersion.value += 1;
  void loadRoot(true);
};

async function loadAllPaginatedItems<T>(
  query: DocumentNode,
  dataKey: "employeeProjects" | "themes",
): Promise<T[]> {
  const limit = 100;
  let offset = 0;
  let hasMore = true;
  const items: T[] = [];

  while (hasMore) {
    const response = await client.query({
      query,
      variables: { offset, limit },
      fetchPolicy: "network-only",
    });

    const page = response.data?.[dataKey];
    const pageItems = (page?.items ?? []) as T[];

    items.push(...pageItems);
    hasMore = Boolean(page?.hasMore);
    offset += pageItems.length;

    if (hasMore && pageItems.length === 0) break;
  }

  return items;
}

const loadFilterOptions = async () => {
  try {
    if (props.projectId) {
      const [projectUsersResponse, themesResponse] = await Promise.all([
        client.query({
          query: GET_PROJECT_USERS,
          variables: { projectId: props.projectId },
          fetchPolicy: "network-only",
        }),
        client.query({
          query: GET_PROJECT_THEMES,
          variables: { id: props.projectId },
          fetchPolicy: "network-only",
        }),
      ]);

      projects.value = [];
      employees.value = (
        projectUsersResponse.data?.project?.accessRights?.items ?? []
      ).map((accessRight: { user: UserOption }) => accessRight.user);

      themes.value = (themesResponse.data?.project?.themes ?? []).map(
        (theme: { id: string; name: string }) => ({
          ...theme,
          project: { id: props.projectId as string },
        }),
      );

      return;
    }

    const [loadedProjects, loadedThemes, employeesResponse] = await Promise.all(
      [
        loadAllPaginatedItems<ProjectOption>(
          GET_EMPLOYEE_PROJECTS,
          "employeeProjects",
        ),
        loadAllPaginatedItems<ThemeOption>(GET_THEMES, "themes"),
        client.query({
          query: GET_EMPLOYEES,
          fetchPolicy: "network-only",
        }),
      ],
    );

    projects.value = loadedProjects;
    themes.value = loadedThemes;
    employees.value = employeesResponse.data?.users ?? [];
  } catch (e) {
    logErrorMessages(e);
    toast.error(t("timesheets.errors.couldNotLoadTimesheets"));
  }
};

watch(
  () => props.projectId,
  (projectId) => {
    filters.projectId = projectId ?? "";
    filters.userId = "";
    filters.thematicId = "";
    void loadFilterOptions();
  },
  { immediate: true },
);

const sortedProjects = computed(() =>
  [...projects.value].sort((a, b) => a.name.localeCompare(b.name)),
);

const sortedEmployees = computed(() =>
  [...employees.value].sort((a, b) =>
    `${a.firstName ?? ""} ${a.lastName ?? ""}`
      .trim()
      .localeCompare(`${b.firstName ?? ""} ${b.lastName ?? ""}`.trim()),
  ),
);

const availableThemes = computed(() => {
  const items = filters.projectId
    ? themes.value.filter((theme) => theme.project?.id === filters.projectId)
    : themes.value;

  return [...items].sort((a, b) => {
    const projectCompare = (a.project?.name ?? "").localeCompare(
      b.project?.name ?? "",
    );

    return projectCompare || a.name.localeCompare(b.name);
  });
});

const periodRange = computed(() => {
  switch (periodMode.value) {
    case "WEEK":
      return {
        fromDate: periodCursor.value.startOf("isoWeek").format("YYYY-MM-DD"),
        toDate: periodCursor.value.endOf("isoWeek").format("YYYY-MM-DD"),
      };

    case "MONTH":
      return {
        fromDate: periodCursor.value.startOf("month").format("YYYY-MM-DD"),
        toDate: periodCursor.value.endOf("month").format("YYYY-MM-DD"),
      };

    case "YEAR":
      return {
        fromDate: periodCursor.value.startOf("year").format("YYYY-MM-DD"),
        toDate: periodCursor.value.endOf("year").format("YYYY-MM-DD"),
      };

    case "CUSTOM":
      return {
        fromDate: filters.customFromDate || null,
        toDate: filters.customToDate || null,
      };

    case "ALL_TIME":
      return {
        fromDate: null,
        toDate: null,
      };

    default:
      return {
        fromDate: null,
        toDate: null,
      };
  }
});

const periodLabel = computed(() => {
  switch (periodMode.value) {
    case "WEEK": {
      const start = periodCursor.value.startOf("isoWeek");
      const end = periodCursor.value.endOf("isoWeek");

      if (start.year() === end.year()) {
        if (start.month() === end.month()) {
          return `${start.format("D")}–${end.format("D MMM YYYY")}`;
        }

        return `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`;
      }

      return `${start.format("D MMM YYYY")} – ${end.format("D MMM YYYY")}`;
    }

    case "MONTH":
      return periodCursor.value.format("MMMM YYYY");

    case "YEAR":
      return periodCursor.value.format("YYYY");

    case "CUSTOM":
      return t("timesheets.customRange");

    case "ALL_TIME":
      return t("timesheets.allTime");

    default:
      return "";
  }
});

const apiFilters = computed(() => ({
  fromDate: periodRange.value.fromDate,
  toDate: periodRange.value.toDate,
  projectIds: filters.projectId ? [filters.projectId] : null,
  userIds: filters.userId ? [filters.userId] : null,
  thematicIds: filters.thematicId ? [filters.thematicId] : null,
  activeOnly: true,
  billable: filters.billable,
  keyword: filters.keyword || null,
}));

const hasMore = computed(() => {
  const loaded = groupBy.value.length
    ? groups.value.length
    : entries.value.length;

  return loaded < totalItems.value;
});

const availableDimensions = computed(() => {
  const all: Array<{
    key: TimesheetGroupDimension;
    label: string;
  }> = [
    {
      key: "PROJECT",
      label: t("timesheets.project"),
    },
    {
      key: "USER",
      label: t("timesheets.user"),
    },
    {
      key: "THEMATIC",
      label: t("timesheets.thematic"),
    },
  ];

  return all.filter((dimension) => !groupBy.value.includes(dimension.key));
});

const dimensionLabel = (dimension: TimesheetGroupDimension) =>
  ({
    PROJECT: t("timesheets.project"),
    USER: t("timesheets.user"),
    THEMATIC: t("timesheets.thematic"),
  })[dimension];

const periodModes: Array<{
  key: PeriodMode;
  label: string;
}> = [
  {
    key: "WEEK",
    label: t("timesheets.week"),
  },
  {
    key: "MONTH",
    label: t("timesheets.month"),
  },
  {
    key: "YEAR",
    label: t("timesheets.year"),
  },
  {
    key: "CUSTOM",
    label: t("timesheets.customRange"),
  },
  {
    key: "ALL_TIME",
    label: t("timesheets.allTime"),
  },
];

const canNavigatePeriod = computed(() =>
  ["WEEK", "MONTH", "YEAR"].includes(periodMode.value),
);

let rootRequestVersion = 0;

const loadRoot = async (reset = false) => {
  if (!reset && loading.value) return;

  const requestVersion = reset ? ++rootRequestVersion : rootRequestVersion;

  loading.value = true;
  error.value = null;

  const offset = reset ? 0 : rootOffset.value;

  if (groupBy.value.length > 0) {
    const response = await client
      .query({
        query: GET_TIMESHEET_GROUPS,
        variables: {
          projectId: props.projectId ?? null,
          filters: apiFilters.value,
          groupBy: groupBy.value,
          level: 0,
          parentGroupKeys: [],
          offset,
          limit: PAGE_SIZE,
        },
        fetchPolicy: "network-only",
      })
      .catch((e) => {
        if (requestVersion === rootRequestVersion) {
          logErrorMessages(e);
          toast.error(t("timesheets.errors.couldNotLoadGroupedTimesheets"));
          loading.value = false;
        }
        return null;
      });

    if (requestVersion !== rootRequestVersion || !response) return;

    const payload = response.data?.timesheetGroups;

    if (payload?.status !== "ok") {
      logErrorMessages(payload?.error);
      toast.error(
        payload?.error || t("timesheets.errors.couldNotLoadGroupedTimesheets"),
      );
      loading.value = false;
      return;
    }

    const data = payload.data;

    groups.value = reset ? data.groups : [...groups.value, ...data.groups];

    entries.value = [];

    totalDuration.value = data.totals.totalDuration;
    totalCount.value = data.totals.totalCount;
    totalItems.value = data.pageInfo.totalItems;
    totalAmount.value = data.totals.totalAmount;

    rootOffset.value = groups.value.length;
  } else {
    const response = await client
      .query({
        query: GET_TIMESHEET_EXPLORER_ENTRIES,
        variables: {
          projectId: props.projectId ?? null,
          filters: apiFilters.value,
          parentGroupKeys: [],
          offset,
          limit: PAGE_SIZE,
        },
        fetchPolicy: "network-only",
      })
      .catch((e) => {
        if (requestVersion === rootRequestVersion) {
          logErrorMessages(e);
          toast.error(t("timesheets.errors.couldNotLoadTimesheets"));
          loading.value = false;
        }
        return null;
      });

    if (requestVersion !== rootRequestVersion || !response) return;

    const payload = response.data?.timesheetExplorerEntries;

    if (payload?.status !== "ok") {
      logErrorMessages(payload?.error);
      toast.error(
        payload?.error || t("timesheets.errors.couldNotLoadTimesheets"),
      );
      loading.value = false;
      return;
    }

    const data = payload.data;

    entries.value = reset ? data.entries : [...entries.value, ...data.entries];

    groups.value = [];

    totalDuration.value = data.totals.totalDuration;
    totalCount.value = data.totals.totalCount;
    totalItems.value = data.pageInfo.totalItems;
    totalAmount.value = data.totals.totalAmount;

    rootOffset.value = entries.value.length;
  }

  loading.value = false;
};

const resetAndReload = () => {
  rootOffset.value = 0;
  groups.value = [];
  entries.value = [];
  totalDuration.value = 0;
  totalCount.value = 0;
  totalItems.value = 0;
  totalAmount.value = 0;

  void loadRoot(true);
};

const setPeriodMode = (mode: PeriodMode) => {
  periodMode.value = mode;

  if (mode === "WEEK" || mode === "MONTH" || mode === "YEAR") {
    periodCursor.value = dayjs();
  }
};

const goToPreviousPeriod = () => {
  switch (periodMode.value) {
    case "WEEK":
      periodCursor.value = periodCursor.value.subtract(1, "week");
      break;

    case "MONTH":
      periodCursor.value = periodCursor.value.subtract(1, "month");
      break;

    case "YEAR":
      periodCursor.value = periodCursor.value.subtract(1, "year");
      break;
  }
};

const goToNextPeriod = () => {
  switch (periodMode.value) {
    case "WEEK":
      periodCursor.value = periodCursor.value.add(1, "week");
      break;

    case "MONTH":
      periodCursor.value = periodCursor.value.add(1, "month");
      break;

    case "YEAR":
      periodCursor.value = periodCursor.value.add(1, "year");
      break;
  }
};

const goToCurrentPeriod = () => {
  periodCursor.value = dayjs();
};

const addGrouping = (dimension: TimesheetGroupDimension) => {
  if (!groupBy.value.includes(dimension)) {
    groupBy.value = [...groupBy.value, dimension];
  }
};

const removeGrouping = (index: number) => {
  groupBy.value = groupBy.value.filter((_, current) => current !== index);
};

const moveGrouping = (index: number, direction: "left" | "right") => {
  const target = direction === "left" ? index - 1 : index + 1;

  if (target < 0 || target >= groupBy.value.length) return;

  const next = [...groupBy.value];

  [next[index], next[target]] = [next[target], next[index]];

  groupBy.value = next;
};

const resetFilters = () => {
  periodMode.value = "MONTH";
  periodCursor.value = dayjs();

  filters.customFromDate = "";
  filters.customToDate = "";
  // Only reset projectId if not fixed by parent component
  if (!props.projectId) {
    filters.projectId = "";
  }
  filters.userId = "";
  filters.thematicId = "";
  filters.billable = undefined;
  filters.keyword = "";
};

watch(
  () => filters.projectId,
  () => {
    if (
      filters.thematicId &&
      !themes.value.some(
        (theme) =>
          theme.id === filters.thematicId &&
          (!filters.projectId || theme.project?.id === filters.projectId),
      )
    ) {
      filters.thematicId = "";
    }
  },
);

let reloadTimer: ReturnType<typeof setTimeout> | null = null;

watch(
  [
    periodMode,
    periodCursor,
    () => filters.customFromDate,
    () => filters.customToDate,
    () => filters.projectId,
    () => filters.userId,
    () => filters.thematicId,
    () => filters.billable,
    groupBy,
  ],
  () => {
    if (reloadTimer) {
      clearTimeout(reloadTimer);
    }

    reloadTimer = setTimeout(resetAndReload, 150);
  },
  {
    deep: true,
    immediate: true,
  },
);

// Watch keyword filter changes and trigger reload
watch(
  () => filters.keyword,
  () => {
    if (reloadTimer) {
      clearTimeout(reloadTimer);
    }

    reloadTimer = setTimeout(resetAndReload, 150);
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)("timesheets.filters")), 1),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(t)("timesheets.period")), 1),
            _createElementVNode("div", _hoisted_7, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(periodModes, (mode) => {
                return _createElementVNode("button", {
                  key: mode.key,
                  type: "button",
                  class: _normalizeClass(["period-mode-button", {
                active: periodMode.value === mode.key,
              }]),
                  onClick: ($event: any) => (setPeriodMode(mode.key))
                }, _toDisplayString(mode.label), 11, _hoisted_8)
              }), 64))
            ])
          ]),
          (canNavigatePeriod.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createElementVNode("button", {
                  type: "button",
                  class: "period-arrow",
                  onClick: goToPreviousPeriod
                }, " ‹ "),
                _createElementVNode("div", _hoisted_10, _toDisplayString(periodLabel.value), 1),
                _createElementVNode("button", {
                  type: "button",
                  class: "period-arrow",
                  onClick: goToNextPeriod
                }, " › "),
                _createVNode(BasicButton, {
                  class: "current-period-button",
                  onClick: goToCurrentPeriod
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)("timesheets.current")), 1)
                  ]),
                  _: 1
                })
              ]))
            : (periodMode.value === 'CUSTOM')
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                  _createElementVNode("label", null, [
                    _createElementVNode("span", null, _toDisplayString(_unref(t)("timesheets.from")), 1),
                    _createVNode(BasicInput, {
                      input: filters.customFromDate,
                      "onUpdate:input": _cache[0] || (_cache[0] = ($event: any) => ((filters.customFromDate) = $event)),
                      "input-id": "timesheet-filter-from",
                      type: "date"
                    }, null, 8, ["input"])
                  ]),
                  _createElementVNode("label", null, [
                    _createElementVNode("span", null, _toDisplayString(_unref(t)("timesheets.to")), 1),
                    _createVNode(BasicInput, {
                      input: filters.customToDate,
                      "onUpdate:input": _cache[1] || (_cache[1] = ($event: any) => ((filters.customToDate) = $event)),
                      "input-id": "timesheet-filter-to",
                      type: "date"
                    }, null, 8, ["input"])
                  ])
                ]))
              : (periodMode.value === 'ALL_TIME')
                ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(_unref(t)("timesheets.allTimesheets")), 1))
                : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_13, [
          (!isProjectFixed.value)
            ? (_openBlock(), _createElementBlock("label", _hoisted_14, [
                _createElementVNode("span", null, _toDisplayString(_unref(t)("timesheets.project")), 1),
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filters.projectId) = $event))
                }, [
                  _createElementVNode("option", _hoisted_15, _toDisplayString(_unref(t)("timesheets.allProjects")), 1),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedProjects.value, (project) => {
                    return (_openBlock(), _createElementBlock("option", {
                      key: project.id,
                      value: project.id
                    }, _toDisplayString(project.name), 9, _hoisted_16))
                  }), 128))
                ], 512), [
                  [_vModelSelect, filters.projectId]
                ])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("label", null, [
            _createElementVNode("span", null, _toDisplayString(_unref(t)("timesheets.user")), 1),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((filters.userId) = $event))
            }, [
              _createElementVNode("option", _hoisted_17, _toDisplayString(_unref(t)("timesheets.allUsers")), 1),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedEmployees.value, (employee) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: employee.id,
                  value: employee.id
                }, _toDisplayString(employee.firstName) + " " + _toDisplayString(employee.lastName), 9, _hoisted_18))
              }), 128))
            ], 512), [
              [_vModelSelect, filters.userId]
            ])
          ]),
          _createElementVNode("label", null, [
            _createElementVNode("span", null, _toDisplayString(_unref(t)("timesheets.thematic")), 1),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((filters.thematicId) = $event))
            }, [
              _createElementVNode("option", _hoisted_19, _toDisplayString(_unref(t)("timesheets.allThematics")), 1),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableThemes.value, (theme) => {
                return (_openBlock(), _createElementBlock("option", {
                  key: theme.id,
                  value: theme.id
                }, [
                  (theme.project?.name)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createTextVNode(_toDisplayString(theme.project.name) + " — ", 1)
                      ], 64))
                    : _createCommentVNode("", true),
                  _createTextVNode(" " + _toDisplayString(theme.name), 1)
                ], 8, _hoisted_20))
              }), 128))
            ], 512), [
              [_vModelSelect, filters.thematicId]
            ])
          ]),
          _createElementVNode("label", _hoisted_21, [
            _createElementVNode("span", null, _toDisplayString(_unref(t)("timesheets.keyword")), 1),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((filters.keyword) = $event)),
              type: "text",
              class: "keyword-input",
              placeholder: _unref(t)('timesheets.keywordPlaceholder')
            }, null, 8, _hoisted_22), [
              [_vModelText, filters.keyword]
            ])
          ]),
          _createElementVNode("label", null, [
            _createElementVNode("span", null, _toDisplayString(_unref(t)("timesheets.billable")), 1),
            _withDirectives(_createElementVNode("select", {
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((filters.billable) = $event))
            }, [
              _createElementVNode("option", _hoisted_23, _toDisplayString(_unref(t)("timesheets.allBillable")), 1),
              _createElementVNode("option", _hoisted_24, _toDisplayString(_unref(t)("timesheets.billableOnly")), 1),
              _createElementVNode("option", _hoisted_25, _toDisplayString(_unref(t)("timesheets.unbillableOnly")), 1)
            ], 512), [
              [_vModelSelect, filters.billable]
            ])
          ]),
          _createVNode(BasicButton, { onClick: resetFilters }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("timesheets.resetFilters")), 1)
            ]),
            _: 1
          })
        ])
      ]),
      _createElementVNode("div", _hoisted_26, [
        _createElementVNode("div", _hoisted_27, _toDisplayString(_unref(t)("timesheets.view")), 1),
        _createElementVNode("div", _hoisted_28, [
          _createElementVNode("span", null, _toDisplayString(_unref(t)("timesheets.groupBy")), 1),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groupBy.value, (dimension, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: dimension,
              class: "group-chip"
            }, [
              _createElementVNode("span", null, _toDisplayString(dimensionLabel(dimension)), 1),
              _createElementVNode("button", {
                type: "button",
                disabled: index === 0,
                onClick: ($event: any) => (moveGrouping(index, 'left'))
              }, " ← ", 8, _hoisted_29),
              _createElementVNode("button", {
                type: "button",
                disabled: index === groupBy.value.length - 1,
                onClick: ($event: any) => (moveGrouping(index, 'right'))
              }, " → ", 8, _hoisted_30),
              _createElementVNode("button", {
                type: "button",
                onClick: ($event: any) => (removeGrouping(index))
              }, "×", 8, _hoisted_31)
            ]))
          }), 128)),
          (availableDimensions.value.length)
            ? (_openBlock(), _createElementBlock("select", {
                key: 0,
                "aria-label": "Add grouping",
                onChange: _cache[7] || (_cache[7] = 
            (event) => {
              const element = event.target as HTMLSelectElement;

              if (element.value) {
                addGrouping(element.value as TimesheetGroupDimension);
              }

              element.value = '';
            }
          )
              }, [
                _createElementVNode("option", _hoisted_32, _toDisplayString(_unref(t)("timesheets.addGrouping")), 1),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableDimensions.value, (dimension) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: dimension.key,
                    value: dimension.key
                  }, _toDisplayString(dimension.label), 9, _hoisted_33))
                }), 128))
              ], 32))
            : _createCommentVNode("", true)
        ])
      ]),
      _createElementVNode("div", _hoisted_34, [
        _createElementVNode("div", _hoisted_35, [
          _createElementVNode("strong", null, _toDisplayString(_unref(formatDurationMinutes)(totalDuration.value)), 1),
          _createElementVNode("span", _hoisted_36, _toDisplayString(formatAmount(totalAmount.value)), 1),
          _createElementVNode("span", null, _toDisplayString(_unref(t)("timesheets.entries", { count: totalCount.value })), 1)
        ]),
        (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_37, _toDisplayString(error.value), 1))
          : (loading.value && groups.value.length === 0 && entries.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_38, _toDisplayString(_unref(t)("timesheets.loading")), 1))
            : (!loading.value && totalCount.value === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_39, _toDisplayString(_unref(t)("timesheets.noResults")), 1))
              : _createCommentVNode("", true),
        (groupBy.value.length)
          ? (_openBlock(true), _createElementBlock(_Fragment, { key: 3 }, _renderList(groups.value, (group) => {
              return (_openBlock(), _createBlock(TimesheetGroupNode, {
                key: group.key,
                group: group,
                path: [group.key],
                level: 0,
                "group-by": groupBy.value,
                filters: apiFilters.value,
                "project-id": __props.projectId,
                "edit-in-modal": editInModal.value,
                "refresh-version": refreshVersion.value,
                onOpenEntry: handleOpenEntry
              }, null, 8, ["group", "path", "group-by", "filters", "project-id", "edit-in-modal", "refresh-version"]))
            }), 128))
          : (_openBlock(), _createElementBlock("div", _hoisted_40, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(entries.value, (entry) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: entry.id,
                  class: _normalizeClass(["entry-row", { 'entry-row--clickable': editInModal.value }]),
                  onClick: ($event: any) => (editInModal.value && handleOpenEntry(entry))
                }, [
                  _createElementVNode("span", null, _toDisplayString(_unref(dayjs)(entry.date).format("DD/MM/YYYY")), 1),
                  _createElementVNode("span", null, _toDisplayString(entry.userName || _unref(t)("timesheets.placeholder.noValue")), 1),
                  _createElementVNode("span", null, _toDisplayString(entry.projectName || _unref(t)("timesheets.placeholder.noValue")), 1),
                  _createElementVNode("span", null, _toDisplayString(entry.thematicName || _unref(t)("timesheets.placeholder.noValue")), 1),
                  _createElementVNode("span", _hoisted_42, _toDisplayString(entry.notes || ""), 1),
                  _createElementVNode("strong", null, _toDisplayString(_unref(formatDurationMinutes)(entry.duration)), 1),
                  _createElementVNode("span", _hoisted_43, _toDisplayString(formatAmount(entry.amount)), 1)
                ], 10, _hoisted_41))
              }), 128))
            ])),
        (hasMore.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_44, [
              _createVNode(BasicButton, {
                disabled: loading.value,
                onClick: _cache[8] || (_cache[8] = ($event: any) => (loadRoot(false)))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(loading.value ? _unref(t)("timesheets.loading") : _unref(t)("timesheets.loadMore")), 1)
                ]),
                _: 1
              }, 8, ["disabled"])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    (showEditTimesheetModal.value && selectedTimesheetForEdit.value)
      ? (_openBlock(), _createBlock(EditTimesheet, {
          key: 0,
          timesheet: selectedTimesheetForEdit.value,
          "theme-only": isDeliveryManager.value && !isUserAdmin.value,
          onClose: closeEditTimesheetModal,
          onDelete: handleTimesheetUpdated,
          "onUpdate:timesheet": handleTimesheetUpdated
        }, null, 8, ["timesheet", "theme-only"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})